<script setup lang="ts">
import { computed } from 'vue'
import { useNuxtApp, useOidcAuth } from '#imports'
import { useMe } from '~/composables/auth/useMe'
import AuthGateShellSkeleton from '~/components/app/AuthGateShellSkeleton.vue'
import { defineAsyncWithSkeleton } from '~/utils/defineAsyncWithSkeleton'

const NoticeAcceptanceWall = defineAsyncWithSkeleton(
  function loadNoticeAcceptanceWall() {
    return import('~/components/app/NoticeAcceptanceWall.vue')
  },
)

interface NoticeGateProps {
  autoFetch?: boolean
}

const props = withDefaults(defineProps<NoticeGateProps>(), {
  autoFetch: true,
})

const { loggedIn } = useOidcAuth()
const { me } = useMe()

// Évite l'avertissement Nuxt « NuxtPage has not been used » sans monter le slot (pages) lorsque la notice bloque.
if (import.meta.dev) {
  const nuxtApp = useNuxtApp() as ReturnType<typeof useNuxtApp> & {
    _isNuxtPageUsed?: boolean
    _isNuxtLayoutUsed?: boolean
  }
  nuxtApp._isNuxtPageUsed = true
  nuxtApp._isNuxtLayoutUsed = true
}

const showNoticeWall = computed(() => {
  if (!props.autoFetch) return false
  if (!loggedIn.value) return false
  if (me.value === null) return false
  return !me.value.noticeUpToDate
})

const showChild = computed(() => {
  if (!props.autoFetch) return true
  if (!loggedIn.value) return true
  if (me.value === null) return false
  return me.value.noticeUpToDate
})
</script>

<template>
  <div class="notice-gate">
    <AuthGateShellSkeleton
      v-if="autoFetch && loggedIn && me === null"
      class="notice-gate__overlay"
    />

    <ClientOnly v-if="showNoticeWall">
      <NoticeAcceptanceWall />
      <template #fallback>
        <div class="notice-gate__client-placeholder">Chargement de la notice…</div>
      </template>
    </ClientOnly>

    <div v-else-if="showChild">
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/spacing" as *;

.notice-gate {
  position: relative;
}

.notice-gate__overlay {
  position: relative;
  z-index: 1;
}

.notice-gate__client-placeholder {
  min-height: 40vh;
  padding: $spacing-xl 0;
  text-align: center;
}
</style>
